import { Component, computed, inject, input, output, signal } from '@angular/core';
import { ToastService } from '@ps-gui/shared-core';
import { UploadFileItemsComponent } from '../upload-file-items/upload-file-items.component';
import { MAX_UPLOAD_SIZE_MB } from './max-upload-size';

function totalSize(files: File[]): number {
  return files.reduce((sum, file) => sum + file.size, 0);
}

@Component({
  selector: 'shared-ui-multi-upload',
  imports: [UploadFileItemsComponent],
  templateUrl: './multi-upload.component.html',
})
export class MultiUploadComponent {
  readonly acceptedFormats = input.required<string[]>();
  readonly acceptedFormatsText = input.required<string>();
  /** All files go up in one request, so the limit applies to their sum, not to each file. */
  readonly allowedMaxTotalSizeMegaBytes = input(MAX_UPLOAD_SIZE_MB);

  readonly filesChanged = output<File[]>();

  private readonly allowedMaxTotalSizeBytes = computed(
    () => this.allowedMaxTotalSizeMegaBytes() * 1024 * 1024,
  );

  private readonly toastService = inject(ToastService);

  protected readonly files = signal<File[]>([]);

  protected preventDefaultAndStopPropagation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  protected onDropFile(event: DragEvent) {
    this.preventDefaultAndStopPropagation(event);
    this.addFiles(event.dataTransfer?.files);
  }

  protected addFile(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    this.addFiles(inputElement.files);
  }

  private addFiles(files?: FileList | null) {
    if (files) {
      const filesArray = Array.from(files);

      for (const file of filesArray) {
        if (!this.acceptedFormats().includes(file.type)) {
          this.toastService.addErrorToast('Dieses Dateiformat wird nicht unterstützt.');
          return;
        }
      }

      if (totalSize([...this.files(), ...filesArray]) > this.allowedMaxTotalSizeBytes()) {
        this.toastService.addErrorToast(
          `Die Dateien dürfen zusammen höchstens ${this.allowedMaxTotalSizeMegaBytes()} MB groß sein.`,
        );
        return;
      }

      filesArray.forEach((file) => this.files().push(file));

      this.filesChanged.emit(this.files());
    }
  }

  protected onClickDeleteFile(file: File) {
    const index = this.files().indexOf(file);
    this.files().splice(index, 1);

    this.filesChanged.emit(this.files());
  }
}
